// 3rd party
import axios from 'axios';

// Bernie
import { getErrorString, Logger, SystemEvent, SystemEventLevel } from 'bernie-logger';

// Models
import { Link } from './link.model';
import { Tracking } from 'modules/tracking';

interface PartnerizeTrackingLinksOptions {
  tracking: Tracking;
  landingPages: Link[];
  logger: Logger;
}

async function getAtlasPartnerizeTrackingLink(options: PartnerizeTrackingLinksOptions): Promise<Link[]> {
  const { tracking, landingPages, logger } = options;
  const { camRef, pubRef, creativeRef } = tracking;

  if (!camRef || !landingPages.length || landingPages.some(({ label, url }) => !label || !url)) {
    logger.logEvent(new SystemEvent(SystemEventLevel.WARN, 'ATLAS_PARTNERIZE_TRACKING_LINKS_INVALID'), {
      tracking,
      landingPages,
    });

    return;
  }

  logger.logEvent(new SystemEvent(SystemEventLevel.INFO, 'ATLAS_PARTNERIZE_TRACKING_LINKS_VALID'), {
    tracking,
    landingPages,
  });

  interface AtlasPartnerizeUrlsRequestData {
    camref: string;
    pubref?: string;
    creativeref?: string;
    landingPages: Link[];
  }

  interface AtlasPartnerizeUrlsResponseData {
    affiliateUrls: Link[];
  }

  const requestData: AtlasPartnerizeUrlsRequestData = {
    camref: camRef,
    pubref: pubRef,
    creativeref: creativeRef,
    landingPages: landingPages,
  };

  try {
    const response = await axios.post<AtlasPartnerizeUrlsResponseData>('/products/api/atlas/urls/pz', requestData);

    const { data: responseData } = response;
    const { affiliateUrls: trackingLinks } = responseData;

    logger.logEvent(
      new SystemEvent(SystemEventLevel.INFO, 'ATLAS_PARTNERIZE_TRACKING_LINKS_GENERATED'),
      { tracking, landingPages },
      { trackingLinks },
      { status: response.status, message: response.statusText }
    );

    return trackingLinks;
  } catch (error) {
    logger.logEvent(
      new SystemEvent(SystemEventLevel.ERROR, 'ATLAS_PARTNERIZE_TRACKING_LINKS_ERROR'),
      { tracking, landingPages },
      getErrorString(error)
    );

    return;
  }
}

interface DirectTrackingLinksOptions {
  landingPages: Link[];
  logger: Logger;
  mdpcid: string;
}

async function getAtlasDirectTrackingLink(options: DirectTrackingLinksOptions): Promise<Link[]> {
  const { landingPages, logger, mdpcid } = options;

  if (!mdpcid || !landingPages.length || landingPages.some(({ label, url }) => !label || !url)) {
    logger.logEvent(new SystemEvent(SystemEventLevel.WARN, 'ATLAS_DIRECT_TRACKING_LINKS_INVALID'), {
      landingPages,
    });

    return;
  }

  logger.logEvent(new SystemEvent(SystemEventLevel.INFO, 'ATLAS_DIRECT_TRACKING_LINKS_VALID'), {
    landingPages,
  });

  interface AtlasDirectUrlsResponseData {
    affiliateUrls: Link[];
  }

  const requestData = { landingPages };

  try {
    const response = await axios.post<AtlasDirectUrlsResponseData>('/products/api/atlas/urls/direct', requestData);

    const { data: responseData } = response;
    const { affiliateUrls: trackingLinks } = responseData;

    logger.logEvent(
      new SystemEvent(SystemEventLevel.INFO, 'ATLAS_DIRECT_TRACKING_LINKS_GENERATED'),
      { landingPages },
      { trackingLinks },
      { status: response.status, message: response.statusText }
    );

    return trackingLinks;
  } catch (error) {
    logger.logEvent(
      new SystemEvent(SystemEventLevel.ERROR, 'ATLAS_DIRECT_TRACKING_LINKS_ERROR'),
      { landingPages },
      getErrorString(error)
    );

    return;
  }
}

export { getAtlasPartnerizeTrackingLink, getAtlasDirectTrackingLink };
